// @flow
import { AppSidebar } from 'bk-web-app/app-sidebar';
import React from 'react';
import { ClientIdContext } from 'bk-web-app';
import { NullState } from 'bench-internal-theme';
import { Query, type QueryRenderProps } from '@apollo/client/react/components';
import { WrappedSpinner } from 'spinner';
import { JournalEntrySidebar } from 'journal-entry-sidebar';
// $FlowFixMe - TS file
import { SelectedDataEntriesProvider } from 'general-ledger';

import compileFollowUpViewData from './compileFollowUpViewData';
import FollowUpView from './FollowUpView';
import type {
  GetLedgerAccountsResponse,
  GetLedgerAccountsVariables,
  GetJEsWithOutstandingCommentsResponse,
  GetJEsWithOutstandingCommentsVariables,
} from './types';
import { GET_LEDGER_ACCOUNTS, GET_JOURNAL_ENTRIES_WITH_OUTSTANDING_COMMENTS } from './queries';

type FollowUpViewContainerProps = {
  search: string,
};

const FollowUpViewContainer = ({ search }: FollowUpViewContainerProps) => (
  <ClientIdContext.Consumer>
    {clientId => (
      <Query query={GET_LEDGER_ACCOUNTS} variables={{ clientId }}>
        {(
          getLedgerAccounts: QueryRenderProps<
            GetLedgerAccountsResponse,
            GetLedgerAccountsVariables,
          >,
        ) => (
          <Query query={GET_JOURNAL_ENTRIES_WITH_OUTSTANDING_COMMENTS} variables={{ clientId }}>
            {(
              getJEsWithOutstandingComments: QueryRenderProps<
                GetJEsWithOutstandingCommentsResponse,
                GetJEsWithOutstandingCommentsVariables,
              >,
            ) => {
              if (getLedgerAccounts.loading || getJEsWithOutstandingComments.loading) {
                return <WrappedSpinner />;
              }

              if (getLedgerAccounts.error || getJEsWithOutstandingComments.error) {
                return (
                  <NullState
                    bigEmoji="🙈"
                    data-test-id="follow-up-view-queries-error"
                    description="Uh oh! There seems to be a problem, sorry!"
                    title="There was a problem loading data for the Follow Up Tab"
                  />
                );
              }
              const ledgerAccounts =
                (getLedgerAccounts.data && getLedgerAccounts.data.ledgerAccounts) || [];

              const journalEntriesWithOutstandingComments =
                (getJEsWithOutstandingComments.data &&
                  getJEsWithOutstandingComments.data.journalEntriesWithOutstandingComments &&
                  getJEsWithOutstandingComments.data.journalEntriesWithOutstandingComments
                    .journalEntries) ||
                [];

              const dataEntriesWithOutstandingComments = compileFollowUpViewData(
                journalEntriesWithOutstandingComments,
                ledgerAccounts,
              );

              if (dataEntriesWithOutstandingComments.length === 0) {
                return (
                  <NullState
                    bigEmoji="🤩"
                    data-test-id="follow-up-view-empty-state"
                    description="Great job on a fantastic customer experience."
                    title="You have no outstanding client comments!"
                  />
                );
              }
              return (
                <SelectedDataEntriesProvider>
                  <AppSidebar>
                    {({ closeSidebar, openSidebar, openerMeta }) => {
                      const selectedDataEntry =
                        openerMeta && openerMeta.id ? openerMeta : undefined;
                      const toggleDataEntry = dataEntry => {
                        const isDataEntryDifferentFromSidebarOpener =
                          dataEntry && dataEntry !== openerMeta;
                        if (isDataEntryDifferentFromSidebarOpener) {
                          openSidebar(
                            <JournalEntrySidebar
                              dismiss={closeSidebar}
                              journalEntryId={dataEntry.journalEntry.id}
                              key={dataEntry.journalEntry.id}
                            />,
                            dataEntry,
                          );
                        } else {
                          closeSidebar();
                        }
                      };
                      return (
                        <FollowUpView
                          dataEntriesWithOutstandingComments={dataEntriesWithOutstandingComments}
                          search={search}
                          selectedDataEntry={selectedDataEntry}
                          toggleDataEntry={toggleDataEntry}
                        />
                      );
                    }}
                  </AppSidebar>
                </SelectedDataEntriesProvider>
              );
            }}
          </Query>
        )}
      </Query>
    )}
  </ClientIdContext.Consumer>
);
export default FollowUpViewContainer;
